import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, Grid, IconButton, InputLabel, MenuItem, Select, Table, TableBody, TableCell, TableHead, TablePagination, TableRow, Typography, tableCellClasses } from '@mui/material';
import styled_ from '@emotion/styled';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DownloadIcon from '@mui/icons-material/Download';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import moment from 'moment';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';
import generatePDF, { Resolution, Margin, Options } from 'react-to-pdf';

import Filters from '../Filters/Filters';
import { useAppDispatch } from '../../store/hooks';
import { ApiStatus } from '../../utils/api.types';
import { fetchEmployeeWeeklyTimesheets, fetchEmployeeTimesheetMonthly } from '../../store/payrollStore/payrollThunk';
import EmployeeTimesheetWeekDrawer from './EmployeeTimesheetWeekDrawer';

const StyledTableCell = styled_(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: '#fff',
    color: '#9da7b7',
    borderTop: '0.25px solid #d9d4d4',
    borderBottom: '0.25px solid #d9d4d4',
    margin: 0,
    padding: 5,
    fontSize: 13,
    paddingLeft: '15px !important',
  },
  [`&.${tableCellClasses.body}`]: {
    verticalAlign: 'super',
    fontSize: 14,
    fontWeight: 'normal',
    color: '#292d35',
    padding: 5,
    border: 'none',
    paddingLeft: '15px !important',
    margin: 0,
  },
}));

const StyledTableRow = styled_(TableRow)(({ theme }) => ({
  '&:nth-of-type(even)': { backgroundColor: '#fbfbfb' },
  '&:nth-of-type(odd)': { backgroundColor: '#e9ecf1' },
  '&:last-child td, &:last-child th': {},
}));

const statusColor: Record<string, { bg: string; fg: string }> = {
  Open: { bg: '#f3f4f6', fg: '#374151' },
  Reopen: { bg: '#f3f4f6', fg: '#374151' },
  Pending: { bg: '#fff7ed', fg: '#c2410c' },
  Closed: { bg: '#e0f2fe', fg: '#0369a1' },
  Approved: { bg: '#edf6ef', fg: '#49a563' },
  Rejected: { bg: '#fbecec', fg: '#c33b3b' },
  Invoiced: { bg: '#ede9fe', fg: '#5b21b6' },
  Scheduled: { bg: '#f3f4f6', fg: '#6b7280' },
};

interface Props {
  employeeId: string;
  employeeName?: string;
  employeeEmpId?: string;
  active?: boolean;
}

interface TimesheetRow {
  timesheetId: string;
  weekStartDate: string;
  weekEndDate: string;
  status: string;
  weekNumber: number;
  month: number;
  year: number;
  title?: string;
  presentDays: number;
  halfDays: number;
  overtimeDays: number;
  totalRegularHours: number;
  totalOvertimeHours: number;
  totalWorkedHours: number;
  totalExpectedDays: number;
  filledDays: number;
  rejectedComments?: string | null;
}

const EmployeeTimesheetTab: FC<Props> = ({ employeeId, employeeName, employeeEmpId, active }) => {
  const dispatch = useAppDispatch();

  const [allRows, setAllRows] = useState<TimesheetRow[]>([]);
  const [status, setStatus] = useState<ApiStatus>(ApiStatus.IDLE);
  const [dateRange, setDateRange] = useState<{ start: string; end: string } | null>(null);
  const [selectedMonth, setSelectedMonth] = useState<string>(''); // '' = all, '01'..'12'

  const [selectedWeek, setSelectedWeek] = useState<TimesheetRow | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [downloading, setDownloading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewDays, setPreviewDays] = useState<any[]>([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  // Preview-scoped month/year selection — independent of the list filter
  const [previewMonth, setPreviewMonth] = useState<string>(''); // '' = All Months, '01'..'12'
  const [previewYear, setPreviewYear] = useState<number>(new Date().getFullYear());

  // Filters.tsx emits a query-string: start=YYYY-MM-DD&end=YYYY-MM-DD&...
  const handleFilterChange = useCallback((qs: string) => {
    const sp = new URLSearchParams(qs);
    const start = sp.get('start');
    const end = sp.get('end');
    if (start && end) setDateRange({ start, end });
  }, []);

  // Fetch full list once per employee (date filtering is client-side because the
  // backend list endpoint doesn't accept a date range, only year/month/week).
  useEffect(() => {
    if (!employeeId || !active) return;
    let cancelled = false;
    (async () => {
      setStatus(ApiStatus.LOADING);
      try {
        const res: any = await dispatch(fetchEmployeeWeeklyTimesheets({ employeeId, page: 1, limit: 200 })).unwrap();
        if (cancelled) return;
        const list: TimesheetRow[] = Array.isArray(res?.timesheets) ? res.timesheets : [];
        setAllRows(list);
        setStatus(ApiStatus.SUCCESS);
      } catch (e) {
        if (cancelled) return;
        setAllRows([]);
        setStatus(ApiStatus.FAILED);
      }
    })();
    return () => { cancelled = true; };
  }, [dispatch, employeeId, active]);

  // Effective range: month override wins over date-range filter.
  const effectiveRange = useMemo(() => {
    if (selectedMonth) {
      // Use year from dateRange.start if available, else current year.
      const year = dateRange?.start ? new Date(dateRange.start).getFullYear() : new Date().getFullYear();
      const monthIdx = parseInt(selectedMonth, 10) - 1;
      const start = new Date(year, monthIdx, 1);
      const end = new Date(year, monthIdx + 1, 0);
      return { start, end };
    }
    if (dateRange) {
      return { start: new Date(dateRange.start), end: new Date(dateRange.end) };
    }
    return null;
  }, [selectedMonth, dateRange]);

  const filteredRows = useMemo(() => {
    if (!effectiveRange) return allRows;
    const startMs = effectiveRange.start.getTime();
    const endMs = effectiveRange.end.getTime() + 24 * 60 * 60 * 1000 - 1;
    return allRows.filter(r => {
      const ws = new Date(r.weekStartDate).getTime();
      return ws >= startMs && ws <= endMs;
    });
  }, [allRows, effectiveRange]);

  // Reset to first page whenever the filter shrinks the list
  useEffect(() => { setPage(0); }, [effectiveRange, allRows.length]);

  const paginatedRows = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredRows.slice(start, start + rowsPerPage);
  }, [filteredRows, page, rowsPerPage]);

  const handleRowView = (row: TimesheetRow) => {
    setSelectedWeek(row);
    setDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setSelectedWeek(null);
  };

  // Resolve which month to download. Priority: explicit month dropdown > current month.
  // (We deliberately don't use the date-range start, because 'This FY Year' would
  // pin the download to April, not the month the user actually cares about.)
  const downloadMonth = useMemo(() => {
    const now = new Date();
    if (selectedMonth) {
      const year = dateRange?.start ? new Date(dateRange.start).getFullYear() : now.getFullYear();
      const m = parseInt(selectedMonth, 10);
      const base = new Date(year, m - 1, 1);
      return { year, month: m, label: moment(base).format('MMM YYYY') };
    }
    return { year: now.getFullYear(), month: now.getMonth() + 1, label: moment(now).format('MMM YYYY') };
  }, [selectedMonth, dateRange]);

  const fetchOneMonthDays = async (year: number, month: number): Promise<any[]> => {
    try {
      const res: any = await dispatch(fetchEmployeeTimesheetMonthly({ employeeId, year, month })).unwrap();
      return Array.isArray(res?.days) ? res.days : [];
    } catch {
      return [];
    }
  };

  // Fetches days for whatever preview-scoped month/year is active.
  // When previewMonth is '' (All Months), pulls all 12 months in parallel and merges.
  const fetchPreviewDays = async (): Promise<any[]> => {
    if (!previewMonth) {
      const results = await Promise.all(
        Array.from({ length: 12 }, (_, i) => fetchOneMonthDays(previewYear, i + 1))
      );
      const merged = results.flat();
      merged.sort((a: any, b: any) => new Date(a.date).getTime() - new Date(b.date).getTime());
      return merged;
    }
    return fetchOneMonthDays(previewYear, parseInt(previewMonth, 10));
  };

  // Reload preview rows whenever the user changes month/year inside the dialog.
  useEffect(() => {
    if (!previewOpen) return;
    let cancelled = false;
    (async () => {
      setPreviewLoading(true);
      try {
        const days = await fetchPreviewDays();
        if (!cancelled) setPreviewDays(days);
      } finally {
        if (!cancelled) setPreviewLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewOpen, previewMonth, previewYear]);

  const openPreview = () => {
    // Seed preview defaults from whatever the user already has selected on the tab
    setPreviewMonth(selectedMonth || String(downloadMonth.month).padStart(2, '0'));
    setPreviewYear(downloadMonth.year);
    setPreviewOpen(true);
  };

  const closePreview = () => {
    setPreviewOpen(false);
    setPreviewDays([]);
  };

  const previewMonthLabel = previewMonth
    ? moment(new Date(previewYear, parseInt(previewMonth, 10) - 1, 1)).format('MMMM YYYY')
    : `All Months ${previewYear}`;
  const isAllMonths = !previewMonth;

  const handleDownloadExcel = async () => {
    setDownloading(true);
    try {
      const days = previewDays.length ? previewDays : await fetchPreviewDays();
      const titleLine = `${employeeName || 'Employee'}${employeeEmpId ? ` (${employeeEmpId})` : ''}`;

      // AOA layout so we can include a title block + totals row, matching the preview.
      // When All Months is selected we prepend a Month column so each row is unambiguous.
      const header = isAllMonths
        ? ['Month', 'Date', 'Day', 'Check In', 'Check Out', 'Reg Hrs', 'OT Hrs', 'Total', 'Label', 'Type']
        : ['Date', 'Day', 'Check In', 'Check Out', 'Reg Hrs', 'OT Hrs', 'Total', 'Label', 'Type'];
      const body = days.map((d: any) => {
        const base = [
          d.date ? moment(d.date).format('DD MMM YYYY') : '-',
          d.day || (d.date ? moment(d.date).format('ddd') : '-'),
          d.checkIn ? moment(d.checkIn).format('hh:mm A') : '-',
          d.checkOut ? moment(d.checkOut).format('hh:mm A') : '-',
          Number(d.regularHours || 0).toFixed(2),
          Number(d.overtimeHours || 0).toFixed(2),
          Number(d.hours || 0).toFixed(2),
          d.label || d.source || '-',
          d.type || '-',
        ];
        return isAllMonths ? [d.date ? moment(d.date).format('MMM YYYY') : '-', ...base] : base;
      });
      const totalReg = days.reduce((s: number, d: any) => s + Number(d.regularHours || 0), 0);
      const totalOt = days.reduce((s: number, d: any) => s + Number(d.overtimeHours || 0), 0);
      const totalAll = days.reduce((s: number, d: any) => s + Number(d.hours || 0), 0);
      const totalsRow = isAllMonths
        ? ['', '', '', '', 'Total', totalReg.toFixed(2), totalOt.toFixed(2), totalAll.toFixed(2), '', '']
        : ['', '', '', 'Total', totalReg.toFixed(2), totalOt.toFixed(2), totalAll.toFixed(2), '', ''];

      const colCount = header.length;
      const aoa: any[][] = [
        ['Monthly Timesheet'],
        [titleLine],
        [`Period: ${previewMonthLabel}`],
        [],
        header,
        ...body,
        totalsRow,
      ];

      const ws = XLSX.utils.aoa_to_sheet(aoa);
      ws['!cols'] = isAllMonths
        ? [{ wch: 12 }, { wch: 14 }, { wch: 6 }, { wch: 11 }, { wch: 11 }, { wch: 10 }, { wch: 10 }, { wch: 10 }, { wch: 22 }, { wch: 12 }]
        : [{ wch: 14 }, { wch: 6 }, { wch: 11 }, { wch: 11 }, { wch: 10 }, { wch: 10 }, { wch: 10 }, { wch: 22 }, { wch: 12 }];
      ws['!merges'] = [
        { s: { r: 0, c: 0 }, e: { r: 0, c: colCount - 1 } },
        { s: { r: 1, c: 0 }, e: { r: 1, c: colCount - 1 } },
        { s: { r: 2, c: 0 }, e: { r: 2, c: colCount - 1 } },
      ];

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Timesheet');
      const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const name = (employeeName || 'Employee').replace(/\s+/g, '_');
      const fileTag = isAllMonths ? `${previewYear}_All` : moment(new Date(previewYear, parseInt(previewMonth, 10) - 1, 1)).format('YYYY_MM');
      saveAs(blob, `Timesheet_${name}_${fileTag}.xlsx`);
    } catch (e) {
      console.error('Excel download failed', e);
      alert('Failed to download report. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  const [pdfDays, setPdfDays] = useState<any[] | null>(null);

  const handleDownloadPdf = async () => {
    setDownloading(true);
    try {
      const days = previewDays.length ? previewDays : await fetchPreviewDays();
      setPdfDays(days);
      await new Promise(r => setTimeout(r, 250));
      const fileTag = isAllMonths ? `${previewYear}_All` : moment(new Date(previewYear, parseInt(previewMonth, 10) - 1, 1)).format('YYYY_MM');
      const pdfOptions: Options = {
        filename: `Timesheet_${(employeeName || 'Employee').replace(/\s+/g, '_')}_${fileTag}.pdf`,
        method: 'save',
        resolution: Resolution.NORMAL,
        page: { margin: Margin.MEDIUM, format: 'A4' },
        canvas: { mimeType: 'image/jpeg', qualityRatio: 1 },
        overrides: { pdf: { compress: true }, canvas: { useCORS: true } },
      };
      await generatePDF(() => document.getElementById('timesheet-pdf-area'), pdfOptions);
    } catch (e) {
      console.error('PDF download failed', e);
      alert('Failed to download PDF. Please try again.');
    } finally {
      setPdfDays(null);
      setDownloading(false);
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ width: '100%', overflow: 'hidden' }} className={'c-card2'}>
        {/* Filter row — sits inside the card, same as Stock Details */}
        <Grid container p={2} alignItems="center">
          <Grid item xs={8.9} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Filters
              type="timesheet"
              filterHandler={handleFilterChange}
              hideFilter
              hidePartySearch
              hideReset
            />
            <FormControl size="small" sx={{ minWidth: 140, ml: 1 }}>
              <InputLabel id="ts-month-label" shrink>Month</InputLabel>
              <Select
                labelId="ts-month-label"
                value={selectedMonth}
                label="Month"
                onChange={(e) => setSelectedMonth(e.target.value as string)}
                displayEmpty
                notched
              >
                <MenuItem value="">All Months</MenuItem>
                <MenuItem value="01">January</MenuItem>
                <MenuItem value="02">February</MenuItem>
                <MenuItem value="03">March</MenuItem>
                <MenuItem value="04">April</MenuItem>
                <MenuItem value="05">May</MenuItem>
                <MenuItem value="06">June</MenuItem>
                <MenuItem value="07">July</MenuItem>
                <MenuItem value="08">August</MenuItem>
                <MenuItem value="09">September</MenuItem>
                <MenuItem value="10">October</MenuItem>
                <MenuItem value="11">November</MenuItem>
                <MenuItem value="12">December</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={3.1} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              startIcon={previewLoading ? <CircularProgress size={14} sx={{ color: '#488dff' }} /> : <DownloadIcon />}
              size="small"
              onClick={openPreview}
              disabled={previewLoading || downloading}
              style={{ color: '#488dff', backgroundColor: '#fff', border: '0.5px solid #488dff', height: 35, borderRadius: 7, whiteSpace: 'nowrap' }}
              variant="contained"
              sx={{ boxShadow: 'none', '&:hover': { boxShadow: 'none' } }}
            >
              Preview & Download {downloadMonth.label}
            </Button>
          </Grid>
        </Grid>

        {filteredRows.length > 0 ? (
          <>
            <Table stickyHeader sx={{ minWidth: 700 }} size="small" aria-label="timesheet table">
              <TableHead>
                <TableRow>
                  <StyledTableCell>Period</StyledTableCell>
                  <StyledTableCell>Month</StyledTableCell>
                  <StyledTableCell>Week #</StyledTableCell>
                  <StyledTableCell>Present</StyledTableCell>
                  <StyledTableCell>Half-day</StyledTableCell>
                  <StyledTableCell>Overtime</StyledTableCell>
                  <StyledTableCell>Regular Hrs</StyledTableCell>
                  <StyledTableCell>OT Hrs</StyledTableCell>
                  <StyledTableCell>Status</StyledTableCell>
                  <StyledTableCell>Actions</StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedRows.map((row) => {
                  const c = statusColor[row.status] || { bg: '#f3f4f6', fg: '#374151' };
                  return (
                    <StyledTableRow key={row.timesheetId}>
                      <StyledTableCell>
                        <span style={{ color: '#488dff', fontWeight: 500 }}>
                          {moment(row.weekStartDate).format('DD MMM')} – {moment(row.weekEndDate).format('DD MMM YYYY')}
                        </span>
                      </StyledTableCell>
                      <StyledTableCell>{moment(row.weekStartDate).format('MMM YYYY')}</StyledTableCell>
                      <StyledTableCell>W{row.weekNumber}</StyledTableCell>
                      <StyledTableCell>{row.presentDays}</StyledTableCell>
                      <StyledTableCell>{row.halfDays}</StyledTableCell>
                      <StyledTableCell>{row.overtimeDays}</StyledTableCell>
                      <StyledTableCell>{Number(row.totalRegularHours || 0).toFixed(2)}</StyledTableCell>
                      <StyledTableCell>{Number(row.totalOvertimeHours || 0).toFixed(2)}</StyledTableCell>
                      <StyledTableCell>
                        <Chip size="small" style={{ borderRadius: 7, color: c.fg, backgroundColor: c.bg }} label={row.status} />
                      </StyledTableCell>
                      <StyledTableCell>
                        <IconButton
                          style={{ color: '#488dff', backgroundColor: '#fff', border: '0.1px solid #488dff', height: 35, width: 35 }}
                          size="small"
                          onClick={() => handleRowView(row)}
                        >
                          <VisibilityIcon style={{ fontSize: 16 }} />
                        </IconButton>
                      </StyledTableCell>
                    </StyledTableRow>
                  );
                })}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={filteredRows.length}
              page={page}
              onPageChange={(_e, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
              rowsPerPageOptions={[10, 25, 50, 100]}
              labelRowsPerPage="Rows per page"
              sx={{
                borderTop: '0.25px solid #d9d4d4',
                backgroundColor: '#fff',
                '& .MuiTablePagination-toolbar': { minHeight: 44, paddingLeft: 2, paddingRight: 1 },
                '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                  fontSize: 13, color: '#545454', margin: 0,
                },
                '& .MuiTablePagination-select': { fontSize: 13, color: '#292d35', paddingTop: '6px', paddingBottom: '6px' },
                '& .MuiTablePagination-actions .MuiIconButton-root': {
                  color: '#488dff',
                  padding: '6px',
                  '&.Mui-disabled': { color: '#cbd5e1' },
                },
              }}
            />
          </>
        ) : status !== ApiStatus.LOADING && (
          <Box sx={{ textAlign: 'center', py: 6, color: '#9da7b7' }}>
            <EventBusyIcon sx={{ fontSize: 48, color: '#cbd5e1', mb: 1 }} />
            <Typography variant="body1" sx={{ fontWeight: 500, color: '#545454' }}>No Timesheets Yet</Typography>
            <Typography variant="caption">
              {dateRange ? 'No timesheets fall in the selected date range. Try widening the filter.' : 'No weekly timesheets have been created for this employee yet.'}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Hidden printable area for PDF export — mirrors the preview dialog look */}
      <div style={{ position: 'fixed', left: -10000, top: 0, width: 820 }}>
        {pdfDays && (() => {
          const totalReg = pdfDays.reduce((s: number, d: any) => s + Number(d.regularHours || 0), 0);
          const totalOt = pdfDays.reduce((s: number, d: any) => s + Number(d.overtimeHours || 0), 0);
          const totalAll = pdfDays.reduce((s: number, d: any) => s + Number(d.hours || 0), 0);
          const cellBase: React.CSSProperties = { padding: '8px 12px', fontSize: 12, color: '#292d35', verticalAlign: 'middle', borderBottom: 'none' };
          const headBase: React.CSSProperties = { padding: '8px 12px', fontSize: 12, color: '#9da7b7', fontWeight: 700, textAlign: 'left', borderBottom: '0.25px solid #d9d4d4', backgroundColor: '#fff' };
          return (
            <div id="timesheet-pdf-area" style={{ padding: 24, background: '#fff', fontFamily: 'Arial, sans-serif', color: '#292d35' }}>
              {/* Title block */}
              <div style={{ marginBottom: 14 }}>
                <h2 style={{ margin: 0, fontSize: 18, color: '#292d35' }}>{isAllMonths ? 'Annual Timesheet' : 'Monthly Timesheet'}</h2>
                <p style={{ margin: '4px 0 0 0', color: '#545454', fontSize: 13 }}>
                  {employeeName || ''}{employeeEmpId ? ` (${employeeEmpId})` : ''}
                </p>
                <p style={{ margin: '2px 0 0 0', color: '#9da7b7', fontSize: 12 }}>
                  Period: {previewMonthLabel}
                </p>
              </div>

              <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #e9ecf1', borderRadius: 6 }}>
                <thead>
                  <tr>
                    {isAllMonths && <th style={headBase}>Month</th>}
                    <th style={headBase}>Date</th>
                    <th style={headBase}>Day</th>
                    <th style={headBase}>Check In</th>
                    <th style={headBase}>Check Out</th>
                    <th style={headBase}>Reg Hrs</th>
                    <th style={headBase}>OT Hrs</th>
                    <th style={headBase}>Total</th>
                    <th style={headBase}>Label</th>
                    <th style={headBase}>Type</th>
                  </tr>
                </thead>
                <tbody>
                  {pdfDays.map((d: any, i: number) => {
                    const bg = i % 2 === 0 ? '#e9ecf1' : '#fbfbfb';
                    return (
                      <tr key={i} style={{ backgroundColor: bg }}>
                        {isAllMonths && <td style={cellBase}>{d.date ? moment(d.date).format('MMM YYYY') : '-'}</td>}
                        <td style={cellBase}>{d.date ? moment(d.date).format('DD MMM YYYY') : '-'}</td>
                        <td style={cellBase}>{d.day || (d.date ? moment(d.date).format('ddd') : '-')}</td>
                        <td style={cellBase}>{d.checkIn ? moment(d.checkIn).format('hh:mm A') : '-'}</td>
                        <td style={cellBase}>{d.checkOut ? moment(d.checkOut).format('hh:mm A') : '-'}</td>
                        <td style={cellBase}>{Number(d.regularHours || 0).toFixed(2)}</td>
                        <td style={cellBase}>{Number(d.overtimeHours || 0).toFixed(2)}</td>
                        <td style={{ ...cellBase, fontWeight: 600 }}>{Number(d.hours || 0).toFixed(2)}</td>
                        <td style={cellBase}>{d.label || d.source || '-'}</td>
                        <td style={cellBase}>{d.type || '-'}</td>
                      </tr>
                    );
                  })}
                  {/* Totals row */}
                  <tr style={{ backgroundColor: '#fafafa', borderTop: '0.5px solid #d9d4d4' }}>
                    {isAllMonths && <td style={cellBase}></td>}
                    <td style={{ ...cellBase, fontWeight: 700 }} colSpan={3}></td>
                    <td style={{ ...cellBase, fontWeight: 700, textAlign: 'right' }}>Total</td>
                    <td style={{ ...cellBase, fontWeight: 700 }}>{totalReg.toFixed(2)}</td>
                    <td style={{ ...cellBase, fontWeight: 700 }}>{totalOt.toFixed(2)}</td>
                    <td style={{ ...cellBase, fontWeight: 700 }}>{totalAll.toFixed(2)}</td>
                    <td style={cellBase}></td>
                    <td style={cellBase}></td>
                  </tr>
                </tbody>
              </table>

              <p style={{ marginTop: 12, color: '#9da7b7', fontSize: 10, textAlign: 'right' }}>
                Generated on {moment().format('DD MMM YYYY, hh:mm A')}
              </p>
            </div>
          );
        })()}
      </div>

      {/* Preview dialog */}
      <Dialog
        open={previewOpen}
        onClose={closePreview}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2, boxShadow: 'none', backgroundImage: 'none', border: '1px solid #e9ecf1' } }}
      >
        <DialogTitle sx={{ borderBottom: '1px solid #e9ecf1', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', backgroundColor: '#fff' }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.125rem', color: '#292d35' }}>
              {employeeName || 'Employee'}{employeeEmpId ? ` (${employeeEmpId})` : ''} — {previewMonthLabel}
            </Typography>
            <Typography variant="caption" sx={{ display: 'block', color: '#9da7b7', fontWeight: 400, mt: 0.25 }}>
              Pick the period you want to download. Choose "All Months" for a yearly report.
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel id="pv-month-label" shrink>Month</InputLabel>
              <Select
                labelId="pv-month-label"
                value={previewMonth}
                label="Month"
                onChange={(e) => setPreviewMonth(e.target.value as string)}
                displayEmpty
                notched
              >
                <MenuItem value="">All Months</MenuItem>
                <MenuItem value="01">January</MenuItem>
                <MenuItem value="02">February</MenuItem>
                <MenuItem value="03">March</MenuItem>
                <MenuItem value="04">April</MenuItem>
                <MenuItem value="05">May</MenuItem>
                <MenuItem value="06">June</MenuItem>
                <MenuItem value="07">July</MenuItem>
                <MenuItem value="08">August</MenuItem>
                <MenuItem value="09">September</MenuItem>
                <MenuItem value="10">October</MenuItem>
                <MenuItem value="11">November</MenuItem>
                <MenuItem value="12">December</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 100 }}>
              <InputLabel id="pv-year-label" shrink>Year</InputLabel>
              <Select
                labelId="pv-year-label"
                value={previewYear}
                label="Year"
                onChange={(e) => setPreviewYear(Number(e.target.value))}
                notched
              >
                {Array.from({ length: 6 }, (_, i) => new Date().getFullYear() - 3 + i).map((y) => (
                  <MenuItem key={y} value={y}>{y}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {previewLoading ? (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <CircularProgress size={28} />
            </Box>
          ) : previewDays.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 6, color: '#9da7b7' }}>
              <Typography variant="body2">No attendance data for {isAllMonths ? `year ${previewYear}` : 'this month'}.</Typography>
            </Box>
          ) : (
            <Table size="small" aria-label="timesheet preview">
              <TableHead>
                <TableRow>
                  {isAllMonths && <StyledTableCell>Month</StyledTableCell>}
                  <StyledTableCell>Date</StyledTableCell>
                  <StyledTableCell>Day</StyledTableCell>
                  <StyledTableCell>Check In</StyledTableCell>
                  <StyledTableCell>Check Out</StyledTableCell>
                  <StyledTableCell>Reg Hrs</StyledTableCell>
                  <StyledTableCell>OT Hrs</StyledTableCell>
                  <StyledTableCell>Total</StyledTableCell>
                  <StyledTableCell>Label</StyledTableCell>
                  <StyledTableCell>Type</StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {previewDays.map((d: any, i: number) => (
                  <StyledTableRow key={i}>
                    {isAllMonths && <StyledTableCell>{d.date ? moment(d.date).format('MMM YYYY') : '-'}</StyledTableCell>}
                    <StyledTableCell>{d.date ? moment(d.date).format('DD MMM YYYY') : '-'}</StyledTableCell>
                    <StyledTableCell>{d.day || (d.date ? moment(d.date).format('ddd') : '-')}</StyledTableCell>
                    <StyledTableCell>{d.checkIn ? moment(d.checkIn).format('hh:mm A') : '-'}</StyledTableCell>
                    <StyledTableCell>{d.checkOut ? moment(d.checkOut).format('hh:mm A') : '-'}</StyledTableCell>
                    <StyledTableCell>{Number(d.regularHours || 0).toFixed(2)}</StyledTableCell>
                    <StyledTableCell>{Number(d.overtimeHours || 0).toFixed(2)}</StyledTableCell>
                    <StyledTableCell><span style={{ fontWeight: 600 }}>{(Number(d.hours) || (Number(d.regularHours || 0) + Number(d.overtimeHours || 0))).toFixed(2)}</span></StyledTableCell>
                    <StyledTableCell>{d.label || d.source || '-'}</StyledTableCell>
                    <StyledTableCell>{d.type || '-'}</StyledTableCell>
                  </StyledTableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions sx={{
          backgroundColor: '#fff',
          borderTop: '1px solid #c1c5cb',
          boxShadow: '0 -2px 8px rgba(0, 0, 0, 0.1)',
          padding: '16px 24px',
          gap: 2,
        }}>
          <Button onClick={closePreview} variant="outlined" sx={{ minWidth: '100px', height: '40px' }}>
            Cancel
          </Button>
          <Button
            onClick={handleDownloadPdf}
            disabled={downloading || previewDays.length === 0}
            startIcon={downloading ? <CircularProgress size={14} /> : null}
            variant="outlined"
            sx={{ minWidth: '100px', height: '40px', textTransform: 'none', color: '#488dff', borderColor: '#488dff' }}
          >
            Download PDF
          </Button>
          <Button
            onClick={handleDownloadExcel}
            disabled={downloading || previewDays.length === 0}
            startIcon={downloading ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : null}
            variant="contained"
            sx={{ minWidth: '100px', height: '40px', textTransform: 'none', boxShadow: 'none', backgroundColor: '#488dff', '&:hover': { boxShadow: 'none', backgroundColor: '#3a7adb' } }}
          >
            Download Excel
          </Button>
        </DialogActions>
      </Dialog>

      <EmployeeTimesheetWeekDrawer
        open={drawerOpen}
        onClose={handleCloseDrawer}
        employeeId={employeeId}
        employeeName={employeeName}
        weekRow={selectedWeek}
        onActionDone={(newStatus) => {
          if (!selectedWeek) return;
          setAllRows(rows => rows.map(r => r.timesheetId === selectedWeek.timesheetId ? { ...r, status: newStatus } : r));
          setSelectedWeek(prev => prev ? { ...prev, status: newStatus } : prev);
        }}
      />
    </Box>
  );
};

export default EmployeeTimesheetTab;
